export const PERMISSIONS = {
  ADVISOR: "advisor",
  DEVELOPER: "developer",
  ADMIN: "admin",
  DESIGNER: "designer",
  COMPLIANCE: "compliance",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const PERMISSION_METADATA: Record<Permission, { name: string; description: string }> = {
  [PERMISSIONS.ADVISOR]: {
    name: "Advisor",
    description: "Access to Clients and Flow instances (default permission)",
  },
  [PERMISSIONS.DEVELOPER]: {
    name: "Developer",
    description: "Access to API Keys and Webhooks",
  },
  [PERMISSIONS.ADMIN]: {
    name: "Admin",
    description: "Access to User Management",
  },
  [PERMISSIONS.DESIGNER]: {
    name: "Designer",
    description:
      "Access to Flows, Risk Models, Portfolios, PDF Templates, Custom Fields, and other design settings",
  },
  [PERMISSIONS.COMPLIANCE]: {
    name: "Compliance",
    description: "Access to compliance checks, audits, and flow instance reviews",
  },
};

export function hasPermission(userPermissions: Permission[], resource: string): boolean {
  const permissions = userPermissions.length > 0 ? userPermissions : [PERMISSIONS.ADVISOR];

  switch (resource) {
    case "api-keys":
    case "webhooks":
      return permissions.includes(PERMISSIONS.DEVELOPER);
    case "users":
      return permissions.includes(PERMISSIONS.ADMIN);
    case "flows":
    case "risk-models":
    case "portfolios":
    case "pdf-templates":
    case "custom-fields":
    case "settings":
      return permissions.includes(PERMISSIONS.DESIGNER);
    case "compliance":
      return permissions.includes(PERMISSIONS.COMPLIANCE);
    case "clients":
    case "flow-instances":
    case "documentation":
      return true;
    default:
      return false;
  }
}

export function hasPermissionType(userPermissions: Permission[], permission: Permission): boolean {
  return userPermissions.includes(permission);
}

/**
 * Compliance reviewers may open flow instances but only in read-only mode,
 * unless they also hold the advisor permission. Returns true when the viewer
 * is restricted to read-only access (compliance without advisor). Enforced on
 * the backend write guards and mirrored in the flow instance UI.
 */
export function isFlowInstanceReadOnlyForUser(userPermissions: string[]): boolean {
  return (
    userPermissions.includes(PERMISSIONS.COMPLIANCE) &&
    !userPermissions.includes(PERMISSIONS.ADVISOR)
  );
}
